import React from 'react';
import { useCurrentFrame, useVideoConfig, interpolate, spring, AbsoluteFill } from 'remotion';
import { COLORS, SUBS, SCENES } from '../constants';
import { SyncedSubtitles } from '../components/SyncedSubtitles';

const DUR = SCENES.CTA.duration;

export const CTAScene: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const fadeIn = interpolate(frame, [0, 10], [0, 1], { extrapolateRight: 'clamp' });
  const fadeOut = interpolate(frame, [DUR - 12, DUR], [1, 0], { extrapolateRight: 'clamp' });

  const titleScale = spring({
    frame: frame - 8,
    fps,
    config: { damping: 10, stiffness: 80, mass: 0.8 },
  });
  const titleOpacity = interpolate(frame, [8, 22], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  const subOpacity = interpolate(frame, [25, 40], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });
  const subY = interpolate(frame, [25, 40], [15, 0], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  const btnScale = spring({
    frame: Math.max(0, frame - 45),
    fps,
    config: { damping: 12, stiffness: 100 },
  });
  const btnOpacity = interpolate(frame, [45, 58], [0, 1], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });
  const btnPulse = 1 + Math.sin(frame * 0.06) * 0.03;

  const particles = Array.from({ length: 40 }, (_, i) => {
    const angle = (i / 40) * Math.PI * 2;
    const dist = 200 + (i % 5) * 60;
    const speed = 0.3 + (i % 3) * 0.2;
    return {
      x: Math.cos(angle + frame * 0.005 * speed) * dist,
      y: Math.sin(angle + frame * 0.005 * speed) * dist,
      size: 2 + (i % 3),
      opacity: 0.2 + Math.sin(frame * 0.05 + i) * 0.15,
    };
  });

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeIn * fadeOut }}>
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: 800,
          height: 800,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${COLORS.brandIndigo}20, ${COLORS.brandTeal}10, transparent)`,
          transform: 'translate(-50%, -50%)',
          filter: 'blur(60px)',
        }}
      />

      {particles.map((p, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `calc(50% + ${p.x}px)`,
            top: `calc(50% + ${p.y}px)`,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            backgroundColor:
              i % 3 === 0 ? COLORS.brandIndigo : i % 3 === 1 ? COLORS.brandTeal : COLORS.brandAmber,
            opacity: p.opacity,
          }}
        />
      ))}

      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24,
          zIndex: 10,
        }}
      >
        <div
          style={{
            fontSize: 64,
            fontWeight: 900,
            fontFamily: 'Inter, system-ui, sans-serif',
            textAlign: 'center',
            lineHeight: 1.15,
            color: COLORS.white,
            opacity: titleOpacity,
            transform: `scale(${titleScale})`,
            maxWidth: 900,
          }}
        >
          Dejá de{' '}
          <span
            style={{
              background: COLORS.gradientBrand,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            perder ventas
          </span>
        </div>

        <div
          style={{
            fontSize: 22,
            fontWeight: 400,
            color: COLORS.gray,
            fontFamily: 'Inter, system-ui, sans-serif',
            textAlign: 'center',
            opacity: subOpacity,
            transform: `translateY(${subY}px)`,
            maxWidth: 600,
            lineHeight: 1.5,
          }}
        >
          Cotizá rápido y con precisión
          <br />
          con inteligencia de precios potenciada por IA
        </div>

        <div
          style={{ marginTop: 16, opacity: btnOpacity, transform: `scale(${btnScale * btnPulse})` }}
        >
          <div
            style={{
              padding: '18px 60px',
              borderRadius: 100,
              background: COLORS.gradientBrand,
              fontSize: 20,
              fontWeight: 700,
              color: COLORS.white,
              fontFamily: 'Inter, system-ui, sans-serif',
              boxShadow: `0 0 40px ${COLORS.primary}40`,
              letterSpacing: 0.5,
            }}
          >
            Agenda tu Demo →
          </div>
        </div>

        <div
          style={{
            fontSize: 16,
            color: COLORS.grayDark,
            fontFamily: 'Inter, system-ui, sans-serif',
            fontWeight: 500,
            opacity: interpolate(frame, [60, 72], [0, 1], {
              extrapolateRight: 'clamp',
              extrapolateLeft: 'clamp',
            }),
            letterSpacing: 2,
          }}
        >
          prixia.com
        </div>
      </div>

      <SyncedSubtitles lines={SUBS.CTA} />
    </AbsoluteFill>
  );
};
